// A porta de entrada. Login, e o segundo fator quando a conta tem.

import { useState } from "react";

import { esquecerMotivo, motivoDaPerda } from "./api/cliente";
import { explicar } from "./api/problema";
import * as sessaoAPI from "./api/sessao";
import type { Sessao } from "./api/tipos";
import { Icone } from "./ui/Icone";
import { Marca } from "./ui/Marca";

/**
 * O que falta preencher, por campo.
 *
 * Antes não havia validação nenhuma: campo vazio ia para o servidor, que
 * respondia 500, e a tela mostrava "Erro interno". A pessoa conclui que o
 * sistema quebrou quando ela apenas não preencheu — e, pior, fica sem saber
 * QUAL dos dois campos falta.
 *
 * A mensagem fica NO CAMPO e não numa faixa no topo: numa tela de dois
 * campos a faixa obriga a pessoa a olhar para cima e mapear de volta.
 *
 * O e-mail exige `@` porque o login é o ENDEREÇO COMPLETO — a causa mais
 * comum de "não consigo entrar" é digitar só a parte antes da arroba. Ver o
 * `unknown user` do Dovecot.
 */
export function faltaPreencher(email: string, senha: string): {
  email?: string;
  senha?: string;
} {
  const erros: { email?: string; senha?: string } = {};
  const e = email.trim();
  if (!e) erros.email = "Digite seu e-mail completo";
  else if (!e.includes("@")) erros.email = "Falta o @: use o endereço completo";
  if (!senha) erros.senha = "Digite a senha";
  return erros;
}

export function Entrar({
  aoEntrar,
  aoDesistir,
}: {
  aoEntrar: (s: Sessao) => void;
  /** Presente só quando já há uma conta aberta e esta tela é "adicionar outra". */
  aoDesistir?: () => void;
}) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [lembrar, setLembrar] = useState(false);
  const [desafio, setDesafio] = useState<string | null>(null);
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState("");
  const [faltando, setFaltando] = useState<{ email?: string; senha?: string }>({});
  const [verSenha, setVerSenha] = useState(false);
  const [ocupado, setOcupado] = useState(false);

  // POR QUE A PESSOA ESTÁ VENDO ESTA TELA. Lido uma vez e esquecido: se
  // ficasse guardado, o aviso reapareceria em todo login seguinte, e aviso
  // que aparece sem motivo é aviso que se aprende a ignorar.
  //
  // `useState` com inicializador, e não leitura solta no corpo: o componente
  // re-renderiza a cada tecla digitada, e ler ali apagaria o motivo na
  // primeira letra do e-mail.
  const [aviso] = useState(() => {
    const m = motivoDaPerda();
    esquecerMotivo();
    return m;
  });

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setErro("");
    if (!desafio) {
      const f = faltaPreencher(email, senha);
      setFaltando(f);
      if (f.email || f.senha) return; // nada vai ao servidor
    }
    setOcupado(true);
    try {
      if (desafio) {
        aoEntrar(await sessaoAPI.verificarSegundoFator(desafio, codigo));
        return;
      }
      const r = await sessaoAPI.entrar(email, senha, lembrar);
      if (r.tipo === "segundo_fator") setDesafio(r.desafio.desafio);
      else aoEntrar(r.sessao);
    } catch (e) {
      setErro(explicar(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-fundo p-4">
      <form onSubmit={enviar} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow">
        <div className="mb-7 flex justify-center">
          <Marca altura={38} />
        </div>

        {desafio ? (
          <>
            <p className="text-sm text-tinta-media mb-4">
              Digite o código do seu aplicativo autenticador.
            </p>
            <label className="block text-sm">
              <span className="text-tinta-fraca">Código de seis dígitos</span>
              <input
                value={codigo}
                onChange={(e) => setCodigo(e.target.value)}
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                className="mt-1 w-full rounded-lg border border-linha px-3 py-2 tracking-[0.3em]"
              />
            </label>
          </>
        ) : (
          <>
            <label className="block text-sm">
              <span className="text-tinta-fraca">Endereço completo</span>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                autoComplete="username"
                autoFocus
                placeholder="voce@seudominio.com.br"
                aria-invalid={!!faltando.email}
                aria-describedby={faltando.email ? "erro-email" : undefined}
                className={`mt-1 w-full rounded-lg border px-3 py-2 ${
                  faltando.email ? "border-perigo" : "border-linha"
                }`}
              />
              {faltando.email && (
                <span id="erro-email" className="mt-1 block text-xs text-perigo">
                  {faltando.email}
                </span>
              )}
            </label>
            <label className="block text-sm mt-4">
              <span className="text-tinta-fraca">Senha</span>
              <span className="relative mt-1 block">
                <input
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  type={verSenha ? "text" : "password"}
                  autoComplete="current-password"
                  aria-invalid={!!faltando.senha}
                  aria-describedby={faltando.senha ? "erro-senha" : undefined}
                  className={`w-full rounded-lg border px-3 py-2 pr-10 ${
                    faltando.senha ? "border-perigo" : "border-linha"
                  }`}
                />
                {/* Mostrar a senha é o que evita a terceira tentativa errada
                    em teclado de celular. `tabIndex={-1}` para não entrar no
                    caminho do Tab entre os campos e o botão Entrar. */}
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setVerSenha((v) => !v)}
                  aria-label={verSenha ? "Ocultar senha" : "Mostrar senha"}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-tinta-fraca"
                >
                  <Icone nome={verSenha ? "EyeOff" : "Eye"} tamanho={16} />
                </button>
              </span>
              {faltando.senha && (
                <span id="erro-senha" className="mt-1 block text-xs text-perigo">
                  {faltando.senha}
                </span>
              )}
            </label>
            <label className="flex items-center gap-2 mt-4 text-sm text-tinta-media">
              <input
                type="checkbox"
                checked={lembrar}
                onChange={(e) => setLembrar(e.target.checked)}
              />
              Manter conectado neste computador
            </label>
          </>
        )}

        {aviso && !erro && (
          <p
            className="mt-4 rounded-lg border border-aviso/40 bg-aviso/10 p-3 text-sm text-tinta"
            role="status"
          >
            {aviso}
          </p>
        )}

        {erro && (
          <p className="mt-4 text-sm text-perigo" role="alert">
            {erro}
          </p>
        )}

        <button
          type="submit"
          disabled={ocupado}
          className="mt-6 w-full rounded-lg bg-acao px-4 py-2.5 text-white disabled:opacity-60"
        >
          {ocupado ? "…" : desafio ? "Confirmar" : "Entrar"}
        </button>

        {/* DEPOIS do botão, não antes. Ele ficava entre o "manter conectado"
            e o Entrar, no meio do caminho de quem está preenchendo — e é uma
            saída, não um passo do fluxo. Saída fica no fim. */}
        {aoDesistir && (
          <button
            type="button"
            onClick={aoDesistir}
            className="mt-4 w-full text-sm text-acao hover:underline"
          >
            Voltar para a conta que já está aberta
          </button>
        )}
      </form>
    </div>
  );
}
